import { useMemo } from "react";

type FrameOverlayProps = {
  frameStyle: string;
  color: string;
  width: number;
  height: number;
};

export function FrameOverlay({ frameStyle, color, width, height }: FrameOverlayProps) {
  const stars = useMemo(() => {
    if (frameStyle !== "galaxy") {
      return [];
    }

    const result: Array<{ x: number; y: number; r: number; opacity: number }> = [];
    let seed = 137;
    const rng = () => {
      seed = (seed * 1664525 + 1013904223) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    for (let index = 0; index < 60; index += 1) {
      const edge = Math.floor(rng() * 4);
      const position = rng();
      const inset = 4 + rng() * 18;
      const x = edge === 1 ? width - inset : edge === 3 ? inset : position * width;
      const y = edge === 0 ? inset : edge === 2 ? height - inset : position * height;
      result.push({ x, y, r: 0.6 + rng() * 1.8, opacity: 0.2 + rng() * 0.7 });
    }

    return result;
  }, [frameStyle, height, width]);

  if (frameStyle === "plain") {
    return null;
  }

  const corners = [
    [0, 0, 1, 1],
    [width, 0, -1, 1],
    [0, height, 1, -1],
    [width, height, -1, -1],
  ] as const;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3 }}
    >
      {frameStyle === "ornate" && (
        <>
          <rect x={8} y={8} width={width - 16} height={height - 16} fill="none" stroke={color} strokeWidth={0.8} rx={15} opacity={0.55} />
          {corners.map(([cx, cy, dx, dy], index) => (
            <g key={index}>
              <rect
                x={cx + dx * 8 - 5}
                y={cy + dy * 8 - 5}
                width={10}
                height={10}
                fill={color}
                opacity={0.88}
                transform={`rotate(45 ${cx + dx * 8} ${cy + dy * 8})`}
              />
            </g>
          ))}
        </>
      )}
      {frameStyle === "circuit" && corners.map(([cx, cy, dx, dy], index) => (
        <g key={index}>
          <path d={`M ${cx + dx * 5} ${cy + dy * 28} L ${cx + dx * 5} ${cy + dy * 5} L ${cx + dx * 28} ${cy + dy * 5}`} fill="none" stroke={color} strokeWidth={2} />
          <circle cx={cx + dx * 5} cy={cy + dy * 28} r={2.5} fill={color} />
          <circle cx={cx + dx * 28} cy={cy + dy * 5} r={2.5} fill={color} />
        </g>
      ))}
      {frameStyle === "runic" && (
        <>
          <rect x={6} y={6} width={width - 12} height={height - 12} fill="none" stroke={color} strokeWidth={1} rx={16} opacity={0.45} strokeDasharray="6 4" />
          {corners.map(([cx, cy, dx, dy], index) => (
            <g key={index}>
              <line x1={cx + dx * 8} y1={cy + dy * 18} x2={cx + dx * 18} y2={cy + dy * 8} stroke={color} strokeWidth={2} strokeLinecap="round" />
              <line x1={cx + dx * 8} y1={cy + dy * 8} x2={cx + dx * 18} y2={cy + dy * 18} stroke={color} strokeWidth={2} strokeLinecap="round" />
            </g>
          ))}
        </>
      )}
      {frameStyle === "crystal" && corners.map(([cx, cy, dx, dy], index) => (
        <g key={index}>
          <line x1={cx + dx * 4} y1={cy + dy * 30} x2={cx + dx * 30} y2={cy + dy * 4} stroke={color} strokeWidth={2.5} />
          <line x1={cx + dx * 4} y1={cy + dy * 20} x2={cx + dx * 20} y2={cy + dy * 4} stroke={color} strokeWidth={1} opacity={0.5} />
        </g>
      ))}
      {frameStyle === "vines" && corners.map(([cx, cy, dx, dy], index) => (
        <g key={index}>
          <path
            d={`M ${cx + dx * 5} ${cy + dy * 36} C ${cx + dx * 5} ${cy + dy * 18} ${cx + dx * 10} ${cy + dy * 9} ${cx + dx * 32} ${cy + dy * 5}`}
            fill="none"
            stroke={color}
            strokeWidth={2.4}
            strokeLinecap="round"
            opacity={0.85}
          />
        </g>
      ))}
      {frameStyle === "flames" && (
        <>
          <polygon points={`0,${height} ${width * 0.15},${height - 18} ${width * 0.3},${height - 8} ${width * 0.5},${height - 20} ${width * 0.7},${height - 10} ${width * 0.85},${height - 18} ${width},${height}`} fill={color} opacity={0.25} />
          <polygon points={`0,0 ${width * 0.16},10 ${width * 0.35},4 ${width * 0.56},12 ${width * 0.78},5 ${width},0`} fill={color} opacity={0.18} />
        </>
      )}
      {frameStyle === "galaxy" && (
        <>
          {stars.map((star, index) => (
            <circle key={index} cx={star.x} cy={star.y} r={star.r} fill="#ffffff" opacity={star.opacity} />
          ))}
          <rect x={2} y={2} width={width - 4} height={height - 4} fill="none" stroke={color} strokeWidth={0.5} rx={18} opacity={0.2} />
        </>
      )}
    </svg>
  );
}
